import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useOrg } from "../../contexts/OrgContext";

export function OrganizationSwitcher() {
  const { user } = useAuth();
  const { activeOrg, switchOrg } = useOrg();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  if (!user || !user.organizations || user.organizations.length === 0) {
    return null;
  }

  const handleOrgClick = (orgId: string) => {
    switchOrg(orgId);
    setIsOpen(false);
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "owner":
        return "#f59e0b"; // amber
      case "admin":
        return "#3b82f6"; // blue
      case "member":
        return "#6b7280"; // gray
      default:
        return "#6b7280";
    }
  };

  return (
    <div className="org-switcher" ref={dropdownRef}>
      <button
        className="org-switcher-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="org-name">{activeOrg?.name || "Select Organization"}</span>
        <svg
          className={`chevron ${isOpen ? "rotate" : ""}`}
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 6L8 10L12 6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="org-switcher-dropdown">
          <div className="org-list">
            {user.organizations.map((org) => (
              <button
                key={org.id}
                className={`org-item ${activeOrg?.id === org.id ? "active" : ""}`}
                onClick={() => handleOrgClick(org.id)}
              >
                <div className="org-item-content">
                  <span className="org-item-name">{org.name}</span>
                  <span
                    className="role-badge"
                    style={{ backgroundColor: getRoleBadgeColor(org.role) }}
                  >
                    {org.role}
                  </span>
                </div>
                {activeOrg?.id === org.id && (
                  <svg
                    className="check-icon"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.3333 4L6 11.3333L2.66667 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
          <div className="org-switcher-footer">
            <Link to="/organizations/create" className="create-org-link" onClick={() => setIsOpen(false)}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8 3.33334V12.6667M3.33333 8H12.6667"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Create Organization
            </Link>
          </div>
        </div>
      )}

      <style>{`
        .org-switcher {
          position: relative;
        }

        .org-switcher-button {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 6px;
          color: white;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .org-switcher-button:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.3);
        }

        .org-name {
          font-weight: 500;
          max-width: 200px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .chevron {
          transition: transform 0.2s;
        }

        .chevron.rotate {
          transform: rotate(180deg);
        }

        .org-switcher-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          left: 0;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          min-width: 280px;
          z-index: 50;
          animation: slideDown 0.2s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .org-list {
          padding: 8px;
          max-height: 300px;
          overflow-y: auto;
        }

        .org-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;
          padding: 10px 12px;
          background: none;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: background-color 0.15s;
          text-align: left;
        }

        .org-item:hover {
          background: #f3f4f6;
        }

        .org-item.active {
          background: #eff6ff;
        }

        .org-item-content {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
        }

        .org-item-name {
          color: #111827;
          font-size: 14px;
          font-weight: 500;
        }

        .role-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 600;
          color: white;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          width: fit-content;
        }

        .check-icon {
          color: #3b82f6;
          flex-shrink: 0;
        }

        .org-switcher-footer {
          border-top: 1px solid #e5e7eb;
          padding: 8px;
        }

        .create-org-link {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          color: #3b82f6;
          text-decoration: none;
          font-size: 14px;
          font-weight: 500;
          border-radius: 6px;
          transition: background-color 0.15s;
        }

        .create-org-link:hover {
          background: #f3f4f6;
        }

        @media (max-width: 768px) {
          .org-name {
            max-width: 120px;
          }

          .org-switcher-dropdown {
            right: 0;
            left: auto;
            min-width: 260px;
          }
        }
      `}</style>
    </div>
  );
}
